import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

/**
 * ExpandableEllipsis (below control)
 * - Collapses to N lines with a subtle right-edge fade
 * - Shows a minimalist "more" / "show less" control *below* the text in grey
 */
export default function ExpandableText({
  text,
  lines = 3,
  variant = "body2",
  preserveLineBreaks = true,
  id,
  moreLabel = "more",
  lessLabel = "show less",
}) {
  const [expanded, setExpanded] = useState(false);
  const [needsToggle, setNeedsToggle] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      if (expanded) {
        setNeedsToggle(true);
        return;
      }
      const overflowing = el.scrollHeight > el.clientHeight + 1;
      setNeedsToggle(overflowing);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, lines, expanded]);

  if (!text)
    return (
      <Typography variant={variant} color="text.secondary">
        -
      </Typography>
    );

  return (
    <Box>
      <Box sx={{ position: "relative" }}>
        <Typography
          id={id}
          ref={ref}
          variant={variant}
          sx={{
            whiteSpace: preserveLineBreaks ? "pre-wrap" : "normal",
            display: expanded ? "block" : "-webkit-box",
            WebkitLineClamp: expanded ? "unset" : lines,
            WebkitBoxOrient: expanded ? "unset" : "vertical",
            overflow: "hidden",
          }}
        >
          {text}
        </Typography>

        {/* Right-edge fade when collapsed */}
        {!expanded && needsToggle && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              right: 0,
              bottom: 0,
              height: "1.6em",
              width: 120,
              pointerEvents: "none",
              background: (t) =>
                `linear-gradient(90deg, ${t.palette.background.paper}00 0%, ${t.palette.background.paper} 60%)`,
            }}
          />
        )}
      </Box>

      {/* Minimal grey control below */}
      {needsToggle && (
        <Box
          role="button"
          aria-controls={id}
          aria-expanded={expanded}
          tabIndex={0}
          onClick={() => setExpanded((v) => !v)}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") && setExpanded((v) => !v)
          }
          sx={{
            mt: 0.5,
            color: (t) => t.palette.text.secondary,
            cursor: "pointer",
            userSelect: "none",
            display: "inline-block",
            textDecoration: "underline",
          }}
        >
          {expanded ? lessLabel : moreLabel}
        </Box>
      )}
    </Box>
  );
}

/* Usage
<LabelValue label="Project Description">
  <ExpandableEllipsis text={project?.description} lines={3} />
</LabelValue>
*/
